<template>
  <Page title="New import">
    <ImportSteps :current="0" class="mb-6" />

    <p v-if="forbidden" class="text-gray-600" data-cy="import-upload-forbidden">
      You don't have access to imports.
    </p>
    <p
      v-else-if="loadError"
      class="text-red-600"
      data-cy="import-upload-load-error"
    >
      {{ loadError }}
    </p>
    <div v-else-if="loading" class="flex justify-center py-10">
      <Spinner message="Loading..." class="text-gray-500" />
    </div>
    <p
      v-else-if="!types.length"
      class="text-gray-600"
      data-cy="import-upload-none"
    >
      You can't create imports.
    </p>

    <div
      v-else
      class="rounded-lg border border-gray-200 bg-white p-4 md:p-5"
      data-cy="import-upload"
    >
      <h2 id="record-type-label" class="mb-2 text-sm font-medium text-gray-900">
        Record type
      </h2>
      <div
        class="grid grid-cols-2 gap-3 md:grid-cols-5"
        role="radiogroup"
        aria-labelledby="record-type-label"
      >
        <button
          v-for="type in types"
          :key="type.record_type"
          type="button"
          role="radio"
          class="flex flex-col items-center gap-2 rounded-lg border px-3 py-5 text-sm"
          :class="
            type.record_type === recordType
              ? 'border-blue-600 bg-blue-50 font-medium text-blue-700'
              : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
          "
          :aria-checked="type.record_type === recordType"
          :data-cy="`record-type-${type.record_type}`"
          @click="selectType(type)"
        >
          <i
            :class="[iconFor(type.record_type), 'text-lg']"
            aria-hidden="true"
          />
          {{ type.label }}
        </button>
      </div>

      <div class="mt-5 grid gap-4 md:grid-cols-2">
        <SelectInput
          v-model="vendorName"
          label="Vendor"
          placeholder="Select a vendor"
          hide-placeholder
          :options="vendorOptions"
          :disabled="loadingVendors || !vendorOptions.length"
          data-cy="vendor"
        />
        <InputField
          v-model="name"
          title="Batch name"
          dense
          data-cy="batch-name"
        />
      </div>
      <p v-if="vendorsError" class="mt-2 text-sm text-red-600" role="alert">
        {{ vendorsError }}
      </p>

      <div
        v-if="selectedVendor && selectedVendor.parameters.length"
        class="mt-4 grid gap-4 md:grid-cols-2"
        data-cy="vendor-parameters"
      >
        <template v-for="param in selectedVendor.parameters" :key="param.name">
          <Checkbox
            v-if="param.type === 'checkbox'"
            :model-value="Boolean(parameters[param.name])"
            :label="param.label"
            :data-cy="param.name"
            @update:model-value="setParameter(param.name, $event)"
          />
          <DynamicInput
            v-else
            :data-cy="param.name"
            :options="param"
            :model-value="parameters[param.name]"
            :is-required="true"
            @update:model-value="setParameter(param.name, $event)"
          />
        </template>
      </div>

      <div
        v-if="selectedVendor"
        class="mt-5 space-y-4"
        data-cy="import-datasets"
      >
        <DatasetUpload
          v-for="dataset in selectedVendor.datasets"
          :key="`${recordType}:${selectedVendor.vendor}:${dataset.name}`"
          :dataset="dataset"
          :file-upload-link="selectedVendor.links.file_upload"
          :label="
            selectedVendor.datasets.length > 1
              ? humanizeDatasetName(dataset.name)
              : undefined
          "
          :hint="hintFor(dataset)"
          :max-bytes="isMapped ? MAX_MAPPED_BYTES : undefined"
          @uploaded="keys[dataset.name] = $event"
          @cleared="delete keys[dataset.name]"
          @busy="busy[dataset.name] = $event"
        />
        <a
          v-if="selectedVendor.template"
          :href="selectedVendor.template"
          class="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline"
          data-cy="download-template"
        >
          <i class="fas fa-file-excel" aria-hidden="true" />
          Download template
        </a>
      </div>

      <p
        v-if="submitError"
        class="mt-3 text-sm text-red-600"
        role="alert"
        data-cy="import-upload-error"
      >
        {{ submitError }}
      </p>

      <div class="mt-6 flex justify-end">
        <SeraButton
          text="Continue"
          :disabled="!canContinue"
          :saving="saving"
          data-cy="import-continue"
          @click="submit"
        />
      </div>
    </div>
  </Page>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { onBeforeRouteLeave, useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";

import SeraButton from "@/components/Button.vue";
import Checkbox from "@/components/Checkbox.vue";
import DynamicInput from "@/components/DynamicInput.vue";
import Page from "@/components/Page.vue";
import InputField from "@/components/Seranova/InputField/InputField.vue";
import SelectInput from "@/components/Seranova/SelectInput/SelectInput.vue";
import Spinner from "@/components/Spinner.vue";
import DatasetUpload from "@/DataImports/flow/DatasetUpload.vue";
import {
  MAX_MAPPED_BYTES,
  normalizeExtensions
} from "@/DataImports/flow/fileExtensions";
import ImportSteps from "@/DataImports/flow/ImportSteps.vue";
import type {
  ImportVendor,
  NewImportType,
  VendorDataset
} from "@/DataImports/flow/types";
import { humanizeDatasetName } from "@/mixins";

const GENERIC_VENDOR = "generic";
// Mapped files are read for their columns on create, which caps them at 50 MB.
const MAPPED_VENDORS = new Set([GENERIC_VENDOR, "house_call_pro"]);

const RECORD_TYPE_ICONS: Record<string, string> = {
  customers: "fas fa-users",
  invoices: "fas fa-file-invoice-dollar",
  equipment: "fas fa-tools",
  memberships: "fas fa-id-card",
  coverage_areas: "fas fa-map-marked-alt"
};

const store = useStore();
const router = useRouter();
const route = useRoute();

const loading = ref(true);
const forbidden = ref(false);
const loadError = ref("");
const types = ref<NewImportType[]>([]);
const recordType = ref<string | null>(null);

const vendors = ref<ImportVendor[]>([]);
const loadingVendors = ref(false);
const vendorsError = ref("");
const vendorName = ref<string | null>(null);

const name = ref<string | null>("");
const parameters = ref<Record<string, unknown>>({});
const keys = ref<Record<string, string>>({});
const busy = ref<Record<string, boolean>>({});
const saving = ref(false);
const submitError = ref("");

const selectedType = computed(
  () =>
    types.value.find((type) => type.record_type === recordType.value) ?? null
);
const selectedVendor = computed(
  () =>
    vendors.value.find((vendor) => vendor.vendor === vendorName.value) ?? null
);
const vendorOptions = computed(() =>
  vendors.value.map((vendor) => ({
    label: humanizeDatasetName(vendor.vendor),
    value: vendor.vendor
  }))
);
const isMapped = computed(() => MAPPED_VENDORS.has(vendorName.value ?? ""));
const uploading = computed(() => Object.values(busy.value).some(Boolean));

const parametersSet = computed(() =>
  (selectedVendor.value?.parameters ?? []).every(
    (param) => param.type === "checkbox" || param.name in parameters.value
  )
);
const canContinue = computed(
  () =>
    !saving.value &&
    !uploading.value &&
    Boolean(name.value?.trim()) &&
    Boolean(selectedVendor.value) &&
    selectedVendor.value!.datasets.every(
      (dataset) => keys.value[dataset.name]
    ) &&
    parametersSet.value
);

const iconFor = (recordTypeKey: string) =>
  RECORD_TYPE_ICONS[recordTypeKey] ?? "fas fa-file";

function hintFor(dataset: VendorDataset): string {
  const extensions = normalizeExtensions(dataset.extensions).join(" or ");
  return isMapped.value ? `${extensions}, up to 50 MB` : extensions;
}

function setParameter(paramName: string, value: unknown) {
  if (value === null || value === undefined || value === "") {
    delete parameters.value[paramName];
  } else {
    parameters.value[paramName] = value;
  }
}

function resetFiles() {
  keys.value = {};
  busy.value = {};
  submitError.value = "";
}

// Files and parameters belong to one vendor; a checkbox starts unchecked, which counts as set.
watch(vendorName, () => {
  resetFiles();
  parameters.value = {};
  (selectedVendor.value?.parameters ?? []).forEach((param) => {
    if (param.type === "checkbox") parameters.value[param.name] = false;
  });
});

let vendorRequest = 0;

function defaultVendor(list: ImportVendor[]): string | null {
  if (list.some((vendor) => vendor.vendor === GENERIC_VENDOR))
    return GENERIC_VENDOR;
  return list.length === 1 ? list[0].vendor : null;
}

async function loadVendors(type: NewImportType) {
  const request = ++vendorRequest;
  loadingVendors.value = true;
  vendorsError.value = "";
  try {
    const { data } = await store.dispatch(
      "fetchDataLoadSources",
      type.sources_url
    );
    if (request !== vendorRequest) return;
    vendors.value = data;
    vendorName.value = defaultVendor(data);
  } catch {
    if (request !== vendorRequest) return;
    vendorsError.value = "Couldn't load vendors. Try again.";
  } finally {
    if (request === vendorRequest) loadingVendors.value = false;
  }
}

async function selectType(type: NewImportType) {
  if (type.record_type === recordType.value) return;
  recordType.value = type.record_type;
  vendors.value = [];
  vendorName.value = null;
  parameters.value = {};
  resetFiles();
  await loadVendors(type);
}

async function submit() {
  if (!canContinue.value || !selectedVendor.value || !selectedType.value)
    return;
  saving.value = true;
  submitError.value = "";

  const files = Object.fromEntries(
    Object.entries(keys.value).map(([dataset, key]) => [
      `${dataset}_s3_key`,
      key
    ])
  );
  try {
    const res = await store.dispatch("createBatch", {
      link: selectedVendor.value.links.batch_create,
      data: {
        name: name.value!.trim(),
        ...files,
        batch_parameters: parameters.value
      },
      ignoreError: true
    });
    const batchId = res?.data?.batch_id;
    if (batchId) {
      router.push(
        selectedType.value.map_page_template.replace(":id", String(batchId))
      );
    } else {
      window.notify.success("Upload started");
      router.push("/imports");
    }
  } catch (error: any) {
    saving.value = false;
    submitError.value =
      error?.response?.data?.message || "Couldn't start the import. Try again.";
  }
}

const warnBeforeUnload = (event: BeforeUnloadEvent) => {
  event.preventDefault();
  event.returnValue = "";
};

// A reload or route change mid-upload would drop the file; only guard while one is in flight.
watch(uploading, (active) => {
  if (active) window.addEventListener("beforeunload", warnBeforeUnload);
  else window.removeEventListener("beforeunload", warnBeforeUnload);
});

onBeforeRouteLeave(
  () =>
    !uploading.value ||
    window.confirm("A file is still uploading. Leave and cancel it?")
);

onBeforeUnmount(() =>
  window.removeEventListener("beforeunload", warnBeforeUnload)
);

onMounted(async () => {
  try {
    const { data } = await store.dispatch("fetchImportsPageData");
    types.value = data.new_imports ?? [];
  } catch (error: any) {
    if (error?.response?.status === 403) forbidden.value = true;
    else loadError.value = "Couldn't load imports. Refresh to try again.";
    return;
  } finally {
    loading.value = false;
  }

  store.dispatch("setBreadcrumbs", [
    { label: "Imports", url: "/imports" },
    { label: "New import" }
  ]);

  const wanted = String(route.query.record_type ?? "");
  const initial =
    types.value.find((type) => type.record_type === wanted) ?? types.value[0];
  if (initial) await selectType(initial);
});
</script>
